import React from 'react';
import { X, Search } from 'lucide-react';
import CategoryIcon from '../common/CategoryIcon';
import SectionLabel from '../common/SectionLabel';

const MobileFiltersDrawer = ({
  isOpen,
  onClose,
  search,
  onSearchChange,
  type,
  onTypeChange,
  categories,
  selectedCategories,
  onToggleCategory,
  uncategorizedOnly,
  onToggleUncategorized,
  dateMode,
  onDateModeChange,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  onResetFilters
}) => {
  const hasActiveFilters = type !== 'all' || selectedCategories.length > 0 || uncategorizedOnly || dateMode === 'custom';

  return (
    <>
      {/* Backdrop */}
      <div
        className={`md:hidden fixed inset-0 z-50 transition-opacity duration-300 ${isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'} bg-black/40 backdrop-blur-sm`}
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        className={`md:hidden fixed inset-x-0 bottom-0 pb-2 z-50 bg-white rounded-t-3xl max-h-[88vh] flex flex-col shadow-2xl transition-transform duration-300 ${isOpen ? 'translate-y-0' : 'translate-y-full'}`}
      >
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 bg-neutral-200 rounded-full" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-neutral-100">
          <h2 className="text-base font-bold text-neutral-800">Filter</h2>
          <button
            onClick={onClose}
            className="p-1.5 text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 rounded-lg transition-all"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          {/* Search */}
          <div>
            <SectionLabel className="block mb-2">Suche</SectionLabel>
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
              <input
                type="text"
                placeholder="Beschreibung suchen..."
                value={search}
                onChange={(e) => onSearchChange(e.target.value)}
                className="block w-full pl-9 pr-3 py-3 border border-neutral-200 rounded-xl text-sm bg-neutral-50 focus:outline-none focus:bg-white focus:ring-2 focus:ring-primary-500 transition-all"
              />
            </div>
          </div>

          {/* Date Mode */}
          <div>
            <SectionLabel className="block mb-2">Zeitraum</SectionLabel>
            <div className="grid grid-cols-3 gap-2">
              {[
                { value: 'month', label: 'Monat' },
                { value: 'all', label: 'Alle' },
                { value: 'custom', label: 'Bereich' }
              ].map(mode => (
                <button
                  key={mode.value}
                  onClick={() => onDateModeChange(mode.value)}
                  className={`py-2.5 rounded-xl text-sm font-semibold transition-all ${
                    dateMode === mode.value
                      ? 'bg-primary-600 text-white shadow-sm'
                      : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                  }`}
                >
                  {mode.label}
                </button>
              ))}
            </div>
            {dateMode === 'custom' && (
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div>
                  <label className="block text-xs text-neutral-500 mb-1 font-medium">Von</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => onStartDateChange(e.target.value)}
                    className="w-full p-2.5 border border-neutral-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-neutral-500 mb-1 font-medium">Bis</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => onEndDateChange(e.target.value)}
                    className="w-full p-2.5 border border-neutral-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Type */}
          <div>
            <SectionLabel className="block mb-2">Typ</SectionLabel>
            <div className="grid grid-cols-3 gap-2">
              {[
                { value: 'all', label: 'Alle' },
                { value: 'expense', label: 'Ausgaben' },
                { value: 'income', label: 'Einnahmen' }
              ].map(t => (
                <button
                  key={t.value}
                  onClick={() => onTypeChange(t.value)}
                  className={`py-2.5 rounded-xl text-sm font-semibold transition-all ${
                    type === t.value
                      ? 'bg-primary-600 text-white shadow-sm'
                      : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <button
              onClick={onToggleUncategorized}
              className={`w-full mt-2 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                uncategorizedOnly
                  ? 'bg-amber-100 text-amber-700 border border-amber-200'
                  : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
              }`}
            >
              Nur Unkategorisiert
            </button>
          </div>

          {/* Categories */}
          {categories.length > 0 && (
            <div>
              <SectionLabel className="block mb-2">Kategorien</SectionLabel>
              <div className="flex flex-wrap gap-2">
                {categories.map(cat => {
                  const isSelected = selectedCategories.find(c => c.id === cat.id);
                  return (
                    <button
                      key={cat.id}
                      onClick={() => onToggleCategory(cat)}
                      className={`flex items-center gap-2.5 p-2 rounded-xl text-sm font-medium transition-all ${
                        isSelected
                          ? 'bg-primary-100 text-primary-800 border border-primary-200 shadow-sm'
                          : 'bg-neutral-50 border border-neutral-200 text-neutral-600 hover:bg-neutral-100'
                      }`}
                    >
                      <CategoryIcon
                        name={cat.icon_name}
                        size={18}
                        className={isSelected ? 'text-primary-600' : 'text-neutral-500'}
                      />
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-neutral-100 safe-area-bottom bg-white">
          <div className="flex gap-3">
            {hasActiveFilters && (
              <button
                onClick={onResetFilters}
                className="flex-1 py-3 rounded-xl text-sm font-semibold text-red-600 bg-red-50 hover:bg-red-100 transition-colors"
              >
                Zurücksetzen
              </button>
            )}
            <button
              onClick={onClose}
              className={`${hasActiveFilters ? 'flex-1' : 'w-full'} py-3 rounded-xl text-sm font-semibold bg-primary-600 text-white hover:bg-primary-700 transition-colors shadow-sm`}
            >
              Fertig
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default MobileFiltersDrawer;
